/**
 * Web Audio SFX: classic Roblox samples (loaded from /sounds/manifest.json) with
 * a synth-beep fallback per sound. Supports POSITIONAL playback — pass a world
 * position and the sound is attenuated by distance, low-passed, and sent to a
 * reverb the farther away it is (so distant gunshots/explosions/spawns sound
 * distant). A master GainNode + volume setting controls overall loudness.
 *
 * Audio-unlock: browsers refuse to start an AudioContext until a real user
 * gesture, so we resume on game start AND on the first interaction anywhere.
 */

export type SfxName =
  | "fire" | "hit" | "kill" | "reload" | "hurt" | "round"
  | "buy" | "deny" | "gameover" | "boss" | "explode" | "powerup" | "upgrade"
  | "throw" | "melee" | "thunder" | "rain" | "jump" | "swap" | "spawn" | "teddy" | "snark" | "bounce" | "elevator"
  | "mercdeath" | "mercrespawn" | "minebeep" | "artillery"
  | "achieve" | "win" // achievement-unlock ping + layer-win fanfare (ported from zombies)
  | "mechastep" | "mechafall" | "mechacrouch" // mecha walker one-shots (foot stomp / topple / crouch+rise servo)
  | "drive" | "turretspin" // continuous LOOPING samples (player's own tank, driven via sustain())
  | "driveField" | "turretspinField"; // same loops, second voice = nearest OTHER tank on the field

interface Vec3 {
  x: number;
  y: number;
  z: number;
}

interface Tone {
  freq: number;
  type: OscillatorType;
  dur: number;
  gain: number;
  slide?: number;
}

const TONES: Partial<Record<SfxName, Tone>> = {
  fire: { freq: 660, type: "square", dur: 0.05, gain: 0.5 },
  hit: { freq: 440, type: "triangle", dur: 0.04, gain: 0.4 },
  kill: { freq: 880, type: "sine", dur: 0.08, gain: 0.6, slide: -300 },
  reload: { freq: 330, type: "square", dur: 0.1, gain: 0.5, slide: 220 },
  hurt: { freq: 200, type: "sawtooth", dur: 0.12, gain: 0.7, slide: -90 },
  round: { freq: 520, type: "sine", dur: 0.18, gain: 0.7, slide: 280 },
  buy: { freq: 600, type: "sine", dur: 0.14, gain: 0.6, slide: 500 },
  deny: { freq: 240, type: "square", dur: 0.12, gain: 0.6, slide: -120 },
  gameover: { freq: 160, type: "sawtooth", dur: 0.5, gain: 0.9, slide: -80 },
  boss: { freq: 90, type: "sawtooth", dur: 0.45, gain: 1.0, slide: 40 },
  explode: { freq: 140, type: "sawtooth", dur: 0.25, gain: 0.95, slide: -60 },
  powerup: { freq: 500, type: "sine", dur: 0.22, gain: 0.7, slide: 700 },
  upgrade: { freq: 300, type: "square", dur: 0.3, gain: 0.7, slide: 900 },
  throw: { freq: 300, type: "square", dur: 0.08, gain: 0.5, slide: 380 },
  melee: { freq: 520, type: "square", dur: 0.07, gain: 0.6, slide: -260 },
  thunder: { freq: 70, type: "sawtooth", dur: 0.9, gain: 1.0, slide: -40 },
  jump: { freq: 420, type: "sine", dur: 0.07, gain: 0.4, slide: 260 },
  swap: { freq: 380, type: "square", dur: 0.06, gain: 0.4, slide: 120 },
  spawn: { freq: 180, type: "sawtooth", dur: 0.12, gain: 0.5, slide: 120 },
  teddy: { freq: 300, type: "triangle", dur: 0.25, gain: 0.5, slide: 200 },
  bounce: { freq: 760, type: "square", dur: 0.05, gain: 0.4, slide: -260 }, // fallback ping
  // 'rain' has no synth tone — it only plays if a real sample is provided.
};

/** Per-sound base volume for SAMPLE playback (synth uses its own Tone.gain). */
const VOL: Partial<Record<SfxName, number>> = {
  fire: 0.45, // shoot sample (also played for enemy shots)
  hit: 0.32, // hit blips quieter
  jump: 0.42, // jumps quieter
  spawn: 0.85, // (further reduced by distance)
  throw: 0.5, // mine-laying sample (it's a hot 0dB clip)
  bounce: 0.5, // ricochet — frequent, keep it moderate
  explode: 0.55, // boom.mp3 is a hot 0dB clip (explode + hurt share it)
  hurt: 0.7, // your own tank exploding — a bit louder
  round: 0.7,
  powerup: 0.7,
  deny: 0.6,
  gameover: 0.8,
  elevator: 0.5, // hot 0dB clip
  mercdeath: 0.6, // hired tank destroyed (hot clip)
  mercrespawn: 0.7, // hired tank redeploys
  minebeep: 1.3, // mine proximity beep — deliberately LOUD (it's a danger warning), positional
  artillery: 0.6, // mortar thunk (hot 0dB clip), positional
  achieve: 0.7, // achievement-unlock ping (ported from zombies)
  win: 0.75, // layer-win fanfare (ported from zombies)
  mechastep: 0.22, // mecha foot stomp — QUIET + throttled (many feet plant/sec), positional
  mechafall: 0.6, // mecha topple crash/groan — one-shot, positional
  mechacrouch: 0.45, // mecha crouch/rise servo whir — one-shot per transition, positional
  drive: 0.18, // tank tread clatter — your own tank driving (quiet; loops via sustain)
  turretspin: 0.12, // turret servo whir — your own turret rotating (quiet, sits under the treads)
  driveField: 0.14, // nearest OTHER tank's treads (a touch quieter than your own)
  turretspinField: 0.1, // nearest other tank's turret servo
};

const MASTER_MAX = 0.6; // master gain at 100% volume

class Sfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private reverb: ConvolverNode | null = null;
  muted = false;
  silent = false; // suppress one-shots without touching the user's mute toggle (title-screen demo)
  private volume = 0.5;
  private last: Partial<Record<SfxName, number>> = {};
  private unlockInstalled = false;
  private samples = new Map<string, AudioBuffer>();
  private loops = new Map<string, AudioBufferSourceNode>();
  private manifestTried = false;
  private listener: Vec3 = { x: 0, y: 0, z: 0 };

  constructor() {
    const saved = parseFloat(localStorage.getItem("zsfxvol") ?? "");
    if (!isNaN(saved)) this.volume = Math.max(0, Math.min(1, saved));
    this.installGestureUnlock();
  }

  /** Where the player/camera is, so positional sounds can be attenuated. */
  setListener(x: number, y: number, z: number) {
    this.listener.x = x;
    this.listener.y = y;
    this.listener.z = z;
  }

  setVolume(v: number) {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.master) this.master.gain.value = this.muted ? 0 : this.volume * MASTER_MAX;
    try {
      localStorage.setItem("zsfxvol", String(this.volume));
    } catch {
      /* ignore */
    }
  }

  getVolume(): number {
    return this.volume;
  }

  /** Resume (or lazily create) the context. Safe to call repeatedly. */
  resume() {
    if (!this.ctx) {
      const AC =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) return;
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.volume * MASTER_MAX;
      this.master.connect(this.ctx.destination);
      // reverb bus for distant sounds
      const ret = this.ctx.createGain();
      ret.gain.value = 0.85;
      ret.connect(this.master);
      this.reverb = this.ctx.createConvolver();
      this.reverb.buffer = this.makeImpulse(1.8, 2.6);
      this.reverb.connect(ret);
    }
    if (this.ctx.state === "suspended") void this.ctx.resume();
    this.loadManifest();
  }

  private makeImpulse(seconds: number, decay: number): AudioBuffer {
    const ctx = this.ctx!;
    const len = Math.floor(ctx.sampleRate * seconds);
    const buf = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let c = 0; c < 2; c++) {
      const d = buf.getChannelData(c);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
    }
    return buf;
  }

  /**
   * Load optional real audio samples from /sounds/manifest.json. Any name present
   * plays its sample instead of the synth beep. Called once on first resume.
   */
  private loadManifest() {
    if (this.manifestTried || !this.ctx) return;
    this.manifestTried = true;
    fetch("/sounds/manifest.json")
      .then((r) => (r.ok ? r.json() : null))
      .then(async (m: Record<string, string> | null) => {
        if (!m || !this.ctx) return;
        for (const [name, url] of Object.entries(m)) {
          try {
            const data = await fetch(url).then((r) => r.arrayBuffer());
            this.samples.set(name, await this.ctx.decodeAudioData(data));
          } catch {
            /* skip a bad entry */
          }
        }
      })
      .catch(() => {});
  }

  /**
   * Build the per-sound output chain for a given distance: lowpass -> gain ->
   * master, plus a reverb send that grows with distance. Returns the node the
   * source should connect into.
   */
  private routeOut(dist: number, baseGain: number): AudioNode {
    const ctx = this.ctx!;
    const g = ctx.createGain();
    // GENTLE volume falloff that carries far — distance is conveyed mostly by muffle + reverb, not by
    // going silent (was 1-dist/160 floored 0.08 → near-silent by ~150u; far too aggressive on big floors).
    g.gain.value = baseGain * Math.max(0.32, 1 - dist / 520);
    g.connect(this.master!);
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    // no muffle up close; highs roll off only past ~130 units, then gradually (the effect "kicks in" later)
    lp.frequency.value = Math.max(1400, 18000 - Math.max(0, dist - 130) * 70);
    lp.connect(g);
    if (this.reverb && dist > 70) {
      const send = ctx.createGain();
      send.gain.value = Math.min(0.45, (dist - 70) / 380); // reverb grows past 70 units
      g.connect(send);
      send.connect(this.reverb);
    }
    return lp;
  }

  private playBuffer(buf: AudioBuffer, dist: number, baseGain: number) {
    const src = this.ctx!.createBufferSource();
    src.buffer = buf;
    src.connect(this.routeOut(dist, baseGain));
    src.start();
  }

  /** Start/stop a looping ambient sample (e.g. rain). No-op without a sample. */
  loop(name: string, on: boolean) {
    if (!this.ctx || !this.master) return;
    if (on) {
      if (this.loops.has(name)) return;
      const buf = this.samples.get(name);
      if (!buf) return;
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      const g = this.ctx.createGain();
      g.gain.value = 0.45;
      src.connect(g).connect(this.master);
      src.start();
      this.loops.set(name, src);
    } else {
      const n = this.loops.get(name);
      if (n) {
        try {
          n.stop();
        } catch {
          /* already stopped */
        }
        this.loops.delete(name);
      }
    }
  }

  // sustained wobbling-sine "laser" hum (one shared voice for all firing lasers)
  private hum: { osc: OscillatorNode; lfo: OscillatorNode; gain: GainNode } | null = null;

  /**
   * Drive the laser enemies' continuous, wobbling sine. Call EVERY frame: `on=true`
   * (with `pos` = the nearest firing laser, for distance) while any beam is live,
   * `on=false` to silence it. A SINGLE persistent voice (one oscillator + a slow LFO
   * warbling its pitch) is reused and just gain-ramped — so N lasers don't stack into a
   * wall of noise, and a brief LOS flicker fades the gain rather than restarting the
   * oscillator (which would click). Built lazily on first use; never torn down.
   */
  laserHum(on: boolean, pos?: Vec3, scale = 1) {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const want = on && !this.muted && !this.silent && ctx.state === "running";
    if (want && !this.hum) {
      const osc = ctx.createOscillator(); // the tone
      osc.type = "sine";
      osc.frequency.value = 340;
      const lfo = ctx.createOscillator(); // warbles the tone's pitch
      lfo.type = "sine";
      lfo.frequency.value = 6.5; // wobble rate
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 120; // wobble depth (±Hz)
      lfo.connect(lfoGain).connect(osc.frequency);
      const gain = ctx.createGain();
      gain.gain.value = 0.0001;
      osc.connect(gain).connect(this.master);
      osc.start(now);
      lfo.start(now);
      this.hum = { osc, lfo, gain };
    }
    if (!this.hum || ctx.state !== "running") return; // nothing built, or context frozen (don't queue ramps on a suspended ctx)
    let target = 0.0001;
    if (want) {
      const dist = pos
        ? Math.hypot(pos.x - this.listener.x, pos.y - this.listener.y, pos.z - this.listener.z)
        : 0;
      target = 0.1 * Math.max(0.4, 1 - dist / 520) * Math.max(0, Math.min(1, scale)); // gentle far-carrying falloff (matches routeOut); scale ramps it up over a laser's charge
    }
    this.hum.gain.gain.setTargetAtTime(target, now, 0.05);
  }

  // persistent looping voices (tank drive, turret servo) — one per name, kept alive + gain-ramped
  private sustains = new Map<string, { src: AudioBufferSourceNode; gain: GainNode }>();

  /**
   * Drive a continuous LOOPING sample (tank movement, turret rotation). Call EVERY
   * frame: `on=true` (with `pos` for distance attenuation) while the action is happening,
   * `on=false` to fade it out. One persistent looping voice per name is built lazily and
   * NEVER torn down — it's just gain-ramped (setTargetAtTime), so toggling on/off rapidly
   * fades smoothly instead of restarting the buffer (which would click). No-op if no real
   * sample was loaded for `name` (loops have no synth fallback).
   */
  sustain(name: SfxName, on: boolean, pos?: Vec3) {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const want = on && !this.muted && !this.silent && ctx.state === "running";
    let voice = this.sustains.get(name);
    if (want && !voice) {
      const buf = this.samples.get(name);
      if (!buf) return; // no sample for this loop — silently skip
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      const gain = ctx.createGain();
      gain.gain.value = 0.0001;
      src.connect(gain).connect(this.master);
      src.start(now);
      voice = { src, gain };
      this.sustains.set(name, voice);
    }
    if (!voice || ctx.state !== "running") return; // nothing built, or context frozen
    let target = 0.0001;
    if (want) {
      const dist = pos
        ? Math.hypot(pos.x - this.listener.x, pos.y - this.listener.y, pos.z - this.listener.z)
        : 0;
      target = (VOL[name] ?? 0.5) * Math.max(0.32, 1 - dist / 520); // gentle far-carrying falloff (matches routeOut)
    }
    voice.gain.gain.setTargetAtTime(target, now, 0.06); // ~180ms fade in/out — no click
  }

  /** Unlock on the first real interaction anywhere on the page. */
  private installGestureUnlock() {
    if (this.unlockInstalled) return;
    this.unlockInstalled = true;
    const unlock = () => {
      this.resume();
      if (this.ctx && this.ctx.state === "running") {
        window.removeEventListener("pointerdown", unlock);
        window.removeEventListener("keydown", unlock);
        window.removeEventListener("click", unlock);
      }
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    window.addEventListener("click", unlock);
  }

  /** Play a sound. Pass a world position for distance-based attenuation/filtering. */
  play(type: SfxName, pos?: Vec3) {
    if (this.muted || this.silent || !this.ctx || !this.master) return;
    if (this.ctx.state !== "running") {
      void this.ctx.resume();
      return; // skip this one; next will play once running
    }
    const now = this.ctx.currentTime;
    if ((type === "fire" || type === "hit" || type === "spawn" || type === "snark" || type === "bounce" || type === "minebeep" || type === "explode" || type === "artillery") && now - (this.last[type] ?? 0) < 0.05) return;
    this.last[type] = now;

    const dist = pos
      ? Math.hypot(pos.x - this.listener.x, pos.y - this.listener.y, pos.z - this.listener.z)
      : 0;

    // prefer a real sample (classic sound) if one was loaded for this name
    const sample = this.samples.get(type);
    if (sample) {
      this.playBuffer(sample, dist, VOL[type] ?? 0.9);
      return;
    }
    if (type === "snark") {
      this.snark(dist); // frantic multi-blip chitter — no single Tone can do it
      return;
    }
    const t = TONES[type];
    if (!t) return; // e.g. "rain" has no synth fallback

    const out = this.routeOut(dist, 1);
    const osc = this.ctx.createOscillator();
    const env = this.ctx.createGain();
    osc.type = t.type;
    osc.frequency.setValueAtTime(t.freq, now);
    if (t.slide) osc.frequency.linearRampToValueAtTime(Math.max(40, t.freq + t.slide), now + t.dur);
    env.gain.setValueAtTime(t.gain, now);
    env.gain.exponentialRampToValueAtTime(0.0001, now + t.dur);
    osc.connect(env).connect(out);
    osc.start(now);
    osc.stop(now + t.dur + 0.02);
  }

  /**
   * A frantic, snark-style chitter: several rapid squeaks with alternating
   * pitch. Synth fallback only — if a real `snark` sample is present in
   * /sounds/manifest.json it's used instead (drop your own legally-obtained file).
   */
  private snark(dist: number) {
    const ctx = this.ctx!;
    const out = this.routeOut(dist, 0.6);
    const now = ctx.currentTime;
    const blip = (t0: number, f0: number, f1: number, dur: number, gain: number) => {
      const osc = ctx.createOscillator();
      const env = ctx.createGain();
      osc.type = "square";
      osc.frequency.setValueAtTime(f0, t0);
      osc.frequency.linearRampToValueAtTime(Math.max(80, f1), t0 + dur);
      env.gain.setValueAtTime(0.0001, t0);
      env.gain.exponentialRampToValueAtTime(gain, t0 + 0.006);
      env.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
      osc.connect(env).connect(out);
      osc.start(t0);
      osc.stop(t0 + dur + 0.01);
    };
    const base = 1050 + Math.random() * 250;
    const n = 4 + ((Math.random() * 2) | 0);
    for (let i = 0; i < n; i++) {
      const up = i % 2 === 0;
      blip(now + i * 0.04, base * (up ? 1 : 0.78), base * (up ? 0.7 : 1.15), 0.028, 0.42);
    }
  }

  /**
   * A short, crisp click for the score odometer's count-up. `p` (0..1) is the
   * tally's progress — the pitch climbs as the number rolls higher, so a long
   * combo "zips" upward satisfyingly. Cheap: three nodes, ~40ms, then collected.
   */
  tick(p: number) {
    if (this.muted || !this.ctx || !this.master || this.ctx.state !== "running") return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const out = this.routeOut(0, 0.8);
    const osc = ctx.createOscillator();
    const env = ctx.createGain();
    osc.type = "square";
    osc.frequency.setValueAtTime(480 + Math.max(0, Math.min(1, p)) * 640, now);
    env.gain.setValueAtTime(0.0001, now);
    env.gain.exponentialRampToValueAtTime(0.2, now + 0.004);
    env.gain.exponentialRampToValueAtTime(0.0001, now + 0.035);
    osc.connect(env).connect(out);
    osc.start(now);
    osc.stop(now + 0.045);
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    // silence the MASTER bus too — gating only new one-shots left loops
    // (rain etc.) playing, which made mute look broken
    if (this.master) this.master.gain.value = this.muted ? 0 : this.volume * MASTER_MAX;
    return this.muted;
  }

  /** Diagnostic for the debug panel / console. */
  get state(): string {
    return this.ctx ? this.ctx.state : "no-ctx";
  }
}

export const sfx = new Sfx();
